import { useAuth } from '../../auth/auth-context';
import { ADMIN_SECTIONS } from '../../lib/admin-sections';
import { NavLinkItem, USER_NAVIGATION, type NavigationItem } from './navigation-items';

interface SidebarEntry extends NavigationItem {
  /** False for an admin section whose API is not built yet. */
  readonly isAvailable: boolean;
}

/**
 * What the sidebar lists for the signed-in user.
 *
 * Administrators (any admin capability from the server) get the admin sections
 * they hold the capability for; everyone else gets Projects and Profile. This is
 * presentation only: the admin routes are guarded as well, and the API refuses
 * every admin request from anyone without the capability.
 */
function useSidebarEntries(): readonly SidebarEntry[] {
  const { canUseAdminArea, can } = useAuth();

  if (!canUseAdminArea) {
    return USER_NAVIGATION.map((item) => ({ ...item, isAvailable: true }));
  }

  return ADMIN_SECTIONS.filter((section) => section.capability === null || can(section.capability));
}

export interface SidebarNavigationProps {
  /** Called after a link is followed, so the mobile drawer can close. */
  readonly onNavigate?: (() => void) | undefined;
}

/**
 * The primary navigation list, shared by the desktop sidebar and the mobile drawer
 * so the two can never disagree. Sections that are not built yet stay listed,
 * marked "Soon", and open a placeholder rather than a screen that can only fail.
 */
export function SidebarNavigation({ onNavigate }: SidebarNavigationProps) {
  const entries = useSidebarEntries();

  return (
    <ul className="flex flex-col gap-1">
      {entries.map((entry) => (
        <li key={entry.to}>
          <NavLinkItem item={entry} onNavigate={onNavigate}>
            {entry.isAvailable ? null : (
              <span className="ml-auto rounded-sm border border-line-subtle px-1.5 py-px text-[0.625rem] uppercase tracking-wider text-ink-muted">
                Soon
              </span>
            )}
          </NavLinkItem>
        </li>
      ))}
    </ul>
  );
}
